import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { fetchApi, logClientError } from '../../utils/api';
import LoadingModal from '../bonus/api-loading-modal';

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const URL_REGEX = /^https?:\/\/.+/i;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const emptyReference = () => ({ name: '', email: '', title: '', founder_id: '' });

// Capitalize as the user types: first letter of each word for names, first
// letter only for titles. Uppercase the user already typed is preserved.
const capitalizeWords = (value) => value.replace(/(^|[\s-])([a-z])/g, (m, sep, ch) => sep + ch.toUpperCase());
const capitalizeFirst = (value) => (value ? value.charAt(0).toUpperCase() + value.slice(1) : value);

const formatReferenceField = (field, value) => {
  if (field === 'name') return capitalizeWords(value);
  if (field === 'title') return capitalizeFirst(value);
  return value;
};

function Finalist() {
  const { id, token } = useParams();
  const navigate = useNavigate();
  const [application, setApplication] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  // Form state
  const [introVideoUrl, setIntroVideoUrl] = useState('');
  const [demoVideoUrl, setDemoVideoUrl] = useState('');
  const [additionalInfo, setAdditionalInfo] = useState('');
  const [sentReferences, setSentReferences] = useState([]);
  // Form view: reference drafts submitted (and emailed) with the finalist form
  const [formRefs, setFormRefs] = useState([]);
  const [formVisibleCount, setFormVisibleCount] = useState(1);
  // Already-submitted view: one-at-a-time draft sent immediately by button
  const [draft, setDraft] = useState(emptyReference());
  const [sendingReference, setSendingReference] = useState(false);
  const [referenceError, setReferenceError] = useState('');
  const idempotencyKeyRef = useRef(null);

  const founders = application?.founders || [];

  // Founders who don't have a reference check sent yet
  const availableFounders = founders.filter(
    f => !sentReferences.some(ref => ref.founder_id === f.id)
  );

  const draftHasContent = !!(draft.name.trim() || draft.email.trim() || draft.title.trim() || draft.founder_id);

  // Auto-assign when only one founder still needs a reference
  const draftFounderId = availableFounders.length === 1 ? availableFounders[0].id : draft.founder_id;

  const draftReadyToSend =
    !!draft.name.trim() && EMAIL_REGEX.test(draft.email.trim()) && !!draftFounderId;

  const founderFirstName = (founderId) =>
    (founders.find(f => f.id === parseInt(founderId, 10)) || {}).first_name || '';

  const refHasContent = (ref) =>
    !!(ref.name.trim() || ref.email.trim() || ref.title.trim() || ref.founder_id);

  const handleFormRefChange = (index, field, value) => {
    const formatted = formatReferenceField(field, value);
    setFormRefs(prev => prev.map((ref, i) => (i === index ? { ...ref, [field]: formatted } : ref)));
  };

  const handleDraftChange = (field, value) => {
    setDraft(prev => ({ ...prev, [field]: formatReferenceField(field, value) }));
  };

  const handleSendReference = async () => {
    setReferenceError('');
    if (!draftReadyToSend || sendingReference) return;

    try {
      setSendingReference(true);
      const response = await fetchApi('api/public/finalist/send-reference', {
        method: 'POST',
        body: JSON.stringify({
          id: parseInt(id, 10),
          token,
          reference: {
            name: draft.name.trim(),
            email: draft.email.trim(),
            title: draft.title.trim() || null,
            founder_id: parseInt(draftFounderId, 10),
          },
        }),
      });
      setSentReferences(prev => [...prev, response.reference]);
      setDraft(emptyReference());
    } catch (err) {
      logClientError('finalist-send-reference', err);
      setReferenceError(err.message || 'Failed to send the reference check email. Please try again.');
    } finally {
      setSendingReference(false);
    }
  };

  useEffect(() => {
    const fetchApplication = async () => {
      try {
        setLoading(true);
        const response = await fetchApi('api/public/finalist', {
          method: 'GET',
          params: { id, token }
        });
        setApplication(response.data);
        const refs = response.data.references || [];
        setSentReferences(refs);
        // One draft slot per founder who doesn't have a reference yet
        const slots = Math.max((response.data.founders || []).length - refs.length, 0);
        setFormRefs(Array.from({ length: slots }, emptyReference));
        if (response.data.already_submitted) {
          setSubmitted(true);
        }
      } catch (err) {
        navigate('/error', { replace: true });
        return;
      } finally {
        setLoading(false);
      }
    };

    const validId = id && /^\d+$/.test(id) && parseInt(id, 10) > 0;
    const validToken = token && UUID_REGEX.test(token);

    if (validId && validToken) {
      fetchApplication();
    } else {
      navigate('/error', { replace: true });
    }
  }, [id, token, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Client-side validation
    if (!introVideoUrl.trim()) {
      setError('Please enter your founder introduction video URL.');
      return;
    }
    if (!URL_REGEX.test(introVideoUrl.trim())) {
      setError('Please enter a valid URL starting with http:// or https://');
      return;
    }
    if (demoVideoUrl.trim() && !URL_REGEX.test(demoVideoUrl.trim())) {
      setError('Demo video URL must be a valid URL starting with http:// or https://');
      return;
    }

    // References: a group with anything filled needs a name, a valid email,
    // and a founder pick (auto-assigned when only one founder needs one)
    const filledReferences = [];
    const usedFounderIds = new Set();
    for (let i = 0; i < formRefs.length; i++) {
      if (!refHasContent(formRefs[i])) continue;
      const ref = {
        name: formRefs[i].name.trim(),
        email: formRefs[i].email.trim(),
        title: formRefs[i].title.trim() || null,
        founder_id: availableFounders.length === 1 ? availableFounders[0].id : formRefs[i].founder_id,
      };
      if (!ref.name || !ref.email || !EMAIL_REGEX.test(ref.email)) {
        setError(`Reference ${i + 1}: please provide a name and a valid email, or clear the fields.`);
        return;
      }
      if (!ref.founder_id) {
        setError(`Reference ${i + 1}: please pick which founder this reference is for.`);
        return;
      }
      ref.founder_id = parseInt(ref.founder_id, 10);
      if (usedFounderIds.has(ref.founder_id)) {
        setError(`Reference ${i + 1}: each founder can only have one reference.`);
        return;
      }
      usedFounderIds.add(ref.founder_id);
      filledReferences.push(ref);
    }

    // Generate idempotency key on first attempt; reuse across retries
    if (!idempotencyKeyRef.current) {
      idempotencyKeyRef.current = crypto.randomUUID();
    }

    try {
      setSubmitting(true);
      const response = await fetchApi('api/public/finalist', {
        method: 'POST',
        body: JSON.stringify({
          id: parseInt(id, 10),
          token,
          intro_video_url: introVideoUrl.trim(),
          demo_video_url: demoVideoUrl.trim() || null,
          additional_info: additionalInfo.trim() || null,
          references: filledReferences,
        }),
        headers: { 'X-Idempotency-Key': idempotencyKeyRef.current },
      });
      setSentReferences(response?.references || []);
      setSubmitted(true);
      setApplication(prev => ({
        ...prev,
        already_submitted: true,
        intro_video_url: introVideoUrl.trim(),
        demo_video_url: demoVideoUrl.trim() || null,
        finalist_additional_info: additionalInfo.trim() || null,
      }));
    } catch (err) {
      // A retry after a timed-out first attempt lands here as 400 "already
      // submitted" — the first attempt DID save, so this is a success:
      // reload the saved submission and show the thank-you view.
      if (err.status === 400 && /already submitted/i.test(err.message || '')) {
        try {
          const response = await fetchApi('api/public/finalist', {
            method: 'GET',
            params: { id, token }
          });
          setApplication(response.data);
          setSentReferences(response.data.references || []);
        } catch (refetchErr) {
          // Refetch failed — fall back to what the user typed
          setApplication(prev => ({
            ...prev,
            already_submitted: true,
            intro_video_url: introVideoUrl.trim(),
            demo_video_url: demoVideoUrl.trim() || null,
            finalist_additional_info: additionalInfo.trim() || null,
          }));
        }
        setSubmitted(true);
        return;
      }
      logClientError('finalist', err);
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Form view: collect references with the finalist form — the emails go out
  // automatically when the form is submitted.
  const renderFormReferencesSection = () => (
    <section className="content-section">
      <h2 className="form-section-title">References (Optional)</h2>
      <p className="info-text">
        Each founder can have one reference. Someone who can speak to them and their work. This is optional but helps us learn more about the founders. Enter their info and we'll email them a short form to fill out in support of that founder.
      </p>

      {sentReferences.map((ref) => (
        <p className="info-text" key={ref.id} style={{ fontSize: '0.9rem', color: 'rgba(255,255,255,0.6)', marginBottom: '0.5rem' }}>
          ✓ Reference check email sent to <strong>{ref.name}</strong> ({ref.email})
          {founderFirstName(ref.founder_id) ? ` — for ${founderFirstName(ref.founder_id)}` : ''}
        </p>
      ))}

      {formRefs.map((ref, index) => {
        // Extra slots stay hidden until requested (or already hold content)
        if (index >= formVisibleCount && !refHasContent(ref)) return null;
        const expanded = refHasContent(ref);
        // Founders already picked by other groups aren't offered again
        const takenFounderIds = new Set(
          formRefs.filter((_, i) => i !== index).map(r => parseInt(r.founder_id, 10)).filter(Boolean)
        );
        return (
          <div key={index} style={{ marginTop: index === 0 ? '0.5rem' : '1.5rem' }}>
            <p className="info-text" style={{ color: 'rgba(255,255,255,0.6)', marginBottom: '0.5rem' }}>
              Reference {sentReferences.length + index + 1}
            </p>
            <div className="form-group">
              <input
                type="text"
                className="form-input"
                placeholder="enter name"
                value={ref.name}
                onChange={(e) => handleFormRefChange(index, 'name', e.target.value)}
                maxLength={200}
                aria-label={`Reference ${index + 1} name`}
              />
            </div>
            {expanded && (
              <div className="fade-in">
                <div className="form-group">
                  <input
                    type="email"
                    className="form-input"
                    placeholder="email"
                    value={ref.email}
                    onChange={(e) => handleFormRefChange(index, 'email', e.target.value)}
                    maxLength={320}
                    aria-label={`Reference ${index + 1} email`}
                  />
                </div>
                <div className="form-group">
                  <input
                    type="text"
                    className="form-input"
                    placeholder="title (e.g. professor, former manager)"
                    value={ref.title}
                    onChange={(e) => handleFormRefChange(index, 'title', e.target.value)}
                    maxLength={200}
                    aria-label={`Reference ${index + 1} title`}
                  />
                </div>
                {availableFounders.length > 1 && (
                  <div className="form-group">
                    <select
                      className="form-input"
                      value={ref.founder_id}
                      onChange={(e) => handleFormRefChange(index, 'founder_id', e.target.value)}
                      aria-label={`Reference ${index + 1} — which founder`}
                      style={{ color: ref.founder_id ? 'white' : 'rgba(255,255,255,0.5)' }}
                    >
                      <option value="">who is this reference for?</option>
                      {availableFounders
                        .filter(f => !takenFounderIds.has(f.id))
                        .map(f => (
                          <option key={f.id} value={f.id}>{f.first_name}</option>
                        ))}
                    </select>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
      {formVisibleCount < formRefs.length &&
        formRefs.slice(0, formVisibleCount).every(refHasContent) && (
        <button
          type="button"
          className="fade-in"
          onClick={() => setFormVisibleCount(count => Math.min(count + 1, formRefs.length))}
          aria-label="Add another reference"
          style={{
            background: 'transparent',
            border: '1px solid rgba(255,255,255,0.3)',
            borderRadius: '4px',
            color: 'rgba(255,255,255,0.8)',
            cursor: 'pointer',
            fontSize: '0.85rem',
            marginTop: '0.5rem',
            padding: '0.25rem 0.75rem',
          }}
        >
          + Add another reference
        </button>
      )}
    </section>
  );

  // Already-submitted view: references are emailed immediately per reference.
  const renderReferencesSection = () => (
    <section className="content-section">
      <h2 className="form-section-title">References (Optional)</h2>
      <p className="info-text">
        Each founder can have one reference. Someone who can speak to them and their work. This is optional but helps us learn more about the founders. Enter their info and we'll email them a short form to fill out in support of that founder.
      </p>

      {sentReferences.map((ref) => (
        <p className="info-text" key={ref.id} style={{ fontSize: '0.9rem', color: 'rgba(255,255,255,0.6)', marginBottom: '0.5rem' }}>
          ✓ Reference check email sent to <strong>{ref.name}</strong> ({ref.email})
          {founderFirstName(ref.founder_id) ? ` — for ${founderFirstName(ref.founder_id)}` : ''}
        </p>
      ))}

      {availableFounders.length > 0 && (
        <div style={{ marginTop: sentReferences.length > 0 ? '1rem' : '0.5rem' }}>
          <p className="info-text" style={{ color: 'rgba(255,255,255,0.6)', marginBottom: '0.5rem' }}>
            Reference {sentReferences.length + 1}
          </p>
          <div className="form-group">
            <input
              type="text"
              className="form-input"
              placeholder="enter name"
              value={draft.name}
              onChange={(e) => handleDraftChange('name', e.target.value)}
              maxLength={200}
              aria-label="Reference name"
            />
          </div>
          {draftHasContent && (
            <div className="fade-in">
              <div className="form-group">
                <input
                  type="email"
                  className="form-input"
                  placeholder="email"
                  value={draft.email}
                  onChange={(e) => handleDraftChange('email', e.target.value)}
                  maxLength={320}
                  aria-label="Reference email"
                />
              </div>
              <div className="form-group">
                <input
                  type="text"
                  className="form-input"
                  placeholder="title (e.g. professor, former manager)"
                  value={draft.title}
                  onChange={(e) => handleDraftChange('title', e.target.value)}
                  maxLength={200}
                  aria-label="Reference title"
                />
              </div>
              {availableFounders.length > 1 && (
                <div className="form-group">
                  <select
                    className="form-input"
                    value={draft.founder_id}
                    onChange={(e) => handleDraftChange('founder_id', e.target.value)}
                    aria-label="Reference — which founder"
                    style={{ color: draft.founder_id ? 'white' : 'rgba(255,255,255,0.5)' }}
                  >
                    <option value="">who is this reference for?</option>
                    {availableFounders.map(f => (
                      <option key={f.id} value={f.id}>{f.first_name}</option>
                    ))}
                  </select>
                </div>
              )}
              {draftReadyToSend && (
                <button
                  type="button"
                  className="fade-in"
                  onClick={handleSendReference}
                  disabled={sendingReference}
                  aria-label="Send reference check email"
                  style={{
                    background: 'transparent',
                    border: '1px solid rgba(255,255,255,0.3)',
                    borderRadius: '4px',
                    color: 'rgba(255,255,255,0.8)',
                    cursor: sendingReference ? 'wait' : 'pointer',
                    fontSize: '0.85rem',
                    marginTop: '0.25rem',
                    padding: '0.4rem 1rem',
                  }}
                >
                  {sendingReference ? 'Sending...' : 'Send Reference Check Email'}
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {referenceError && (
        <p className="info-text" style={{ color: 'rgba(255, 0, 0, 0.8)', marginTop: '0.75rem' }} role="alert">
          {referenceError}
        </p>
      )}
    </section>
  );

  return (
    <>
      <Helmet>
        <title>LeapYear | Finalist Info</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <LoadingModal isOpen={submitting} message="Submitting your information..." />

      <main className="container" role="main">
        {loading && (
          <div className="finalist-loader">
            <div className="finalist-loader-dot" />
          </div>
        )}

        {!loading && application && submitted && (
          <section className="content-section fade-in" aria-labelledby="finalist-title">
            <div className="finalist-welcome">
              <h1 className="page-title" id="finalist-title">Thank You!</h1>
              <p className="info-text">
                We've received your finalist submission for <strong>{application.project_name}</strong>.
              </p>
              <p className="info-text">
                We will review and be in touch.
              </p>
              <hr className="light-divider" />
              <h2 className="form-section-title">Your Submission</h2>
              <div className="form-group">
                <p className="info-text" style={{ marginBottom: '0.25rem', color: 'rgba(255,255,255,0.6)' }}>Founder Intro Video</p>
                <p className="info-text"><a href={application.intro_video_url} target="_blank" rel="noopener noreferrer" style={{ color: 'white' }}>{application.intro_video_url}</a></p>
              </div>
              {application.demo_video_url && (
                <div className="form-group">
                  <p className="info-text" style={{ marginBottom: '0.25rem', color: 'rgba(255,255,255,0.6)' }}>Product Demo</p>
                  <p className="info-text"><a href={application.demo_video_url} target="_blank" rel="noopener noreferrer" style={{ color: 'white' }}>{application.demo_video_url}</a></p>
                </div>
              )}
              {application.finalist_additional_info && (
                <div className="form-group">
                  <p className="info-text" style={{ marginBottom: '0.25rem', color: 'rgba(255,255,255,0.6)' }}>Additional Info</p>
                  <p className="info-text">{application.finalist_additional_info}</p>
                </div>
              )}
              <hr className="light-divider" />
            </div>
            {/* References stay actionable after submission */}
            {renderReferencesSection()}
          </section>
        )}

        {!loading && application && !submitted && (
          <form onSubmit={handleSubmit} className="fade-in">
            {/* Welcome Section */}
            <section className="content-section" aria-labelledby="finalist-title">
              <div className="finalist-welcome">
                <p className="section-label finalist-page-title">Finalist Application - {application.app_cycle || 'LeapYear'}</p>
                <h1 className="page-title" id="finalist-title">
                  Next Steps for {application.project_name}
                </h1>
                <p className="info-text">
                  You're a finalist for the {application.app_cycle || 'LeapYear'} cycle and in the top 10% of applications we have reviewed. We ask all our finalists to share a short video introducing themselves. From there, we will review and extend interview invites for the final round.
                </p>
              </div>
            </section>

            <hr className="light-divider" />

            {/* Founder Intro Video */}
            <section className="content-section">
              <h2 className="form-section-title required">Founder Intro Video</h2>
              <p className="info-text">
                Record a 1-2 minute video introducing yourselves, what you're building, and why you're building it. Use Loom, YouTube, or any online platform.
              </p>
              <p className="info-text" style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.9rem' }}>
                Our goal is to learn more about you as people. Text only tells us so much.
              </p>
              <div className="form-group">
                <input
                  type="url"
                  className={`form-input${error && !introVideoUrl.trim() ? ' error' : ''}`}
                  placeholder="paste video link here..."
                  value={introVideoUrl}
                  onChange={(e) => setIntroVideoUrl(e.target.value)}
                  aria-label="Founder introduction video URL"
                />
              </div>
            </section>

            <hr className="light-divider" />

            {/* Demo Video */}
            <section className="content-section">
              <h2 className="form-section-title">Product Demo (Optional)</h2>
              <p className="info-text">
                If you have a working product or prototype, share a 1-2 minute demo video. If your idea is in the idea stage, feel free to share a demo of something else you've built.
              </p>
              <p className="info-text" style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.9rem' }}>
                Our goal is to learn more about you as builders. Text only tells us so much.
              </p>
              <div className="form-group">
                <input
                  type="url"
                  className="form-input"
                  placeholder="paste video link here..."
                  value={demoVideoUrl}
                  onChange={(e) => setDemoVideoUrl(e.target.value)}
                  aria-label="Product demo video URL"
                />
              </div>
            </section>

            <hr className="light-divider" />

            {/* References — emailed automatically at submission */}
            {renderFormReferencesSection()}

            <hr className="light-divider" />

            {/* Additional Info */}
            <section className="content-section">
              <h2 className="form-section-title">Anything Else? (Optional)</h2>
              <div className="form-group">
                <textarea
                  className="form-input"
                  placeholder="let us know if there's anything else we should know"
                  value={additionalInfo}
                  onChange={(e) => setAdditionalInfo(e.target.value)}
                  rows={4}
                  maxLength={5000}
                  aria-label="Additional information"
                  style={{ resize: 'vertical', minHeight: '100px' }}
                />
              </div>
            </section>

            {/* Error message */}
            {error && (
              <p className="info-text" style={{ color: 'rgba(255, 0, 0, 0.8)' }} role="alert">
                {error}
              </p>
            )}

            {/* Submit */}
            <button type="submit" className="submit-button" disabled={submitting}>
              Submit
            </button>
            {formRefs.some(refHasContent) && (
              <p className="info-text fade-in" style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.9rem', marginTop: '0.75rem' }}>
                Upon submission, your references will receive emails, so feel free to give them a heads up as well.
              </p>
            )}
          </form>
        )}
        <div style={{ height: '8rem' }} />
      </main>
    </>
  );
}

export default Finalist;
